import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session timeouts in milliseconds
const SESSION_TIMEOUT_DESKTOP = 30 * 60 * 1000;       // 30 minutes
const SESSION_TIMEOUT_MOBILE = 10 * 60 * 1000;        // 10 minutes (mobile/PWA)

const getSessionTimeout = () => {
  try {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/m/')) {
      return SESSION_TIMEOUT_MOBILE;
    }
  } catch { /* noop */ }
  return SESSION_TIMEOUT_DESKTOP;
};

// Back-compat alias used in the synchronous init code below
const SESSION_TIMEOUT = SESSION_TIMEOUT_DESKTOP;

// Pre-configure axios for better performance
axios.defaults.timeout = 10000; // 10 second timeout
axios.defaults.headers.common['Content-Type'] = 'application/json';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // Defensive parser: zombie localStorage states (e.g. youse_user='undefined' from
  // an older buggy build) must NOT crash the app. We clean up and start anonymous.
  const safeReadUser = () => {
    try {
      const raw = localStorage.getItem('youse_user');
      if (!raw || raw === 'undefined' || raw === 'null') return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      // Bad JSON — purge everything so we don't keep retrying.
      try {
        localStorage.removeItem('youse_token');
        localStorage.removeItem('youse_refresh_token');
        localStorage.removeItem('youse_user');
        localStorage.removeItem('youse_last_activity');
      } catch { /* noop */ }
      return null;
    }
  };
  const safeReadToken = () => {
    const t = localStorage.getItem('youse_token');
    return t && t !== 'undefined' && t !== 'null' ? t : null;
  };

  const [user, setUser] = useState(() => safeReadUser());
  
  const [loading, setLoading] = useState(() => {
    // Check if we have stored auth data
    const token = safeReadToken();
    const userData = safeReadUser();
    const lastActivity = localStorage.getItem('youse_last_activity');
    
    if (token && userData) {
      // Check if session has expired
      if (lastActivity) {
        const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
        if (timeSinceLastActivity > SESSION_TIMEOUT) {
          // Session expired, clear everything
          localStorage.removeItem('youse_token');
          localStorage.removeItem('youse_refresh_token');
          localStorage.removeItem('youse_user');
          localStorage.removeItem('youse_last_activity');
          return false;
        }
      }
      // Valid session - set axios header immediately
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return false; // Not loading if we have valid data
    }
    return false; // Not loading if no data
  });
  
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = safeReadToken();
    const userData = safeReadUser();
    const lastActivity = localStorage.getItem('youse_last_activity');
    
    if (token && userData) {
      if (lastActivity) {
        const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
        if (timeSinceLastActivity > SESSION_TIMEOUT) {
          return false;
        }
      }
      return true;
    }
    return false;
  });
  
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const [sessionExpired, setSessionExpired] = useState(false);

  // Function to handle logout due to inactivity
  const logoutDueToInactivity = useCallback(async () => {
    console.log('[SESSION] User logged out due to inactivity');
    
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('youse_token');
    localStorage.removeItem('youse_refresh_token');
    localStorage.removeItem('youse_user');
    localStorage.removeItem('youse_last_activity');
    delete axios.defaults.headers.common['Authorization'];
    
    setUser(null);
    setIsAuthenticated(false);
    setSessionExpired(true);
  }, []);

  const dismissSessionExpired = useCallback(() => setSessionExpired(false), []);

  // Reset the inactivity timer (uses dynamic timeout based on current route)
  const resetInactivityTimer = useCallback(() => {
    if (!isAuthenticated) return;
    
    lastActivityRef.current = Date.now();
    localStorage.setItem('youse_last_activity', Date.now().toString());
    
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    inactivityTimerRef.current = setTimeout(() => {
      logoutDueToInactivity();
    }, getSessionTimeout());
  }, [isAuthenticated, logoutDueToInactivity]);

  // Check for inactivity on app load - now only sets up the timer
  useEffect(() => {
    if (isAuthenticated) {
      lastActivityRef.current = Date.now();
      localStorage.setItem('youse_last_activity', Date.now().toString());
    }
  }, [isAuthenticated]);

  // Set up activity listeners when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    // Events that indicate user activity
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Throttle the reset function to avoid excessive calls
    let throttleTimeout = null;
    const throttledReset = () => {
      if (throttleTimeout) return;
      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
        resetInactivityTimer();
      }, 1000); // Throttle to once per second
    };

    // Reset timer whenever the user navigates (so the mobile/desktop timeout
    // is re-evaluated when the route prefix changes)
    const handleRouteChange = () => resetInactivityTimer();
    window.addEventListener('popstate', handleRouteChange);
    // Patch pushState/replaceState to emit our own event (no SPA library hook needed)
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      const ret = origPush.apply(this, args);
      handleRouteChange();
      return ret;
    };
    window.history.replaceState = function (...args) {
      const ret = origReplace.apply(this, args);
      handleRouteChange();
      return ret;
    };

    // Add listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Start the initial timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledReset);
      });
      window.removeEventListener('popstate', handleRouteChange);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [isAuthenticated, resetInactivityTimer]);

  const login = async (email, password, tokenData = null) => {
    // If tokenData is provided (from 2FA verification or check-credentials with admins),
    // it MUST contain real tokens. Otherwise we never create a session.
    if (tokenData) {
      const { access_token, refresh_token, user: userData } = tokenData;
      if (!access_token || !userData) {
        // Caller passed an incomplete payload — likely a 2FA challenge response.
        // Refuse to create a session.
        throw new Error('Invalid login payload: missing access token');
      }
      
      localStorage.setItem('youse_token', access_token);
      localStorage.setItem('youse_refresh_token', refresh_token);
      localStorage.setItem('youse_user', JSON.stringify(userData));
      localStorage.setItem('youse_last_activity', Date.now().toString());
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      setIsAuthenticated(true);
      
      return userData;
    }
    
    // Direct login call (no pre-checked tokenData). Backend returns either:
    //   - tokens (ADMIN / COMPLIANCE)
    //   - { requires_2fa: true } for normal users (NO access_token in response)
    // If the backend returns a 2FA challenge, we MUST NOT create a session — instead
    // surface an explicit error so the UI can route the user to the 2FA screen.
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const data = response.data || {};

    if (data.requires_2fa || !data.access_token || !data.user) {
      const err = new Error('Two-factor verification required');
      err.code = 'REQUIRES_2FA';
      err.email_masked = data.email_masked;
      throw err;
    }

    const { access_token, refresh_token, user: userData } = data;
    localStorage.setItem('youse_token', access_token);
    localStorage.setItem('youse_refresh_token', refresh_token);
    localStorage.setItem('youse_user', JSON.stringify(userData));
    localStorage.setItem('youse_last_activity', Date.now().toString());
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(userData);
    setIsAuthenticated(true);
    
    return userData;
  };

  const register = async (data) => {
    // Register user but DO NOT auto-login
    // User will receive activation email and must wait for approval
    const response = await axios.post(`${API_URL}/auth/register`, data);
    
    // Return the response data (contains success message, not tokens)
    return response.data;
  };

  const logout = async () => {
    // Clear the inactivity timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('youse_token');
    localStorage.removeItem('youse_refresh_token');
    localStorage.removeItem('youse_user');
    localStorage.removeItem('youse_last_activity');
    delete axios.defaults.headers.common['Authorization'];
    
    setUser(null);
    setIsAuthenticated(false);
  };

  const refreshToken = async () => {
    const refresh = localStorage.getItem('youse_refresh_token');
    if (!refresh) return false;
    
    try {
      const response = await axios.post(`${API_URL}/auth/refresh`, null, {
        params: { refresh_token: refresh }
      });
      
      const { access_token, refresh_token: newRefresh } = response.data;
      localStorage.setItem('youse_token', access_token);
      localStorage.setItem('youse_refresh_token', newRefresh);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return access_token;
    } catch (error) {
      logout();
      return false;
    }
  };

  // ----------------------------------------------------------------------
  // Axios interceptor: auto-refresh access_token on 401 and retry once.
  // Without this, every API call after token expiry returns "Token expired"
  // and the user sees an empty balance / "Token expired" toast.
  // ----------------------------------------------------------------------
  useEffect(() => {
    // Inline queue so multiple concurrent 401s don't trigger multiple refreshes
    let isRefreshing = false;
    let pendingRequests = [];

    const onRefreshed = (newToken) => {
      pendingRequests.forEach((cb) => cb(newToken));
      pendingRequests = [];
    };

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config || {};
        const status = error.response?.status;
        const url = original.url || '';

        // Don't try to refresh for auth endpoints themselves
        const isAuthEndpoint = /\/auth\/(login|refresh|register|logout)/.test(url);

        if (status === 401 && !isAuthEndpoint && !original._retry) {
          original._retry = true;

          if (isRefreshing) {
            // Wait for the in-flight refresh, then retry with the new token
            return new Promise((resolve, reject) => {
              pendingRequests.push((newToken) => {
                if (!newToken) return reject(error);
                original.headers = original.headers || {};
                original.headers['Authorization'] = `Bearer ${newToken}`;
                resolve(axios(original));
              });
            });
          }

          isRefreshing = true;
          try {
            const newToken = await refreshToken();
            isRefreshing = false;
            if (!newToken) {
              onRefreshed(null);
              return Promise.reject(error);
            }
            onRefreshed(newToken);
            original.headers = original.headers || {};
            original.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(original);
          } catch (e) {
            isRefreshing = false;
            onRefreshed(null);
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('youse_user', JSON.stringify(userData));
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/me`);
      // Extract user from response.data.user since /api/user/me returns { user: {...}, profile: {...}, ... }
      const userData = response.data.user || response.data;
      setUser(userData);
      localStorage.setItem('youse_user', JSON.stringify(userData));
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  const isAdmin = () => {
    return user?.role === 'ADMIN' || user?.role === 'COMPLIANCE';
  };

  const isCommercialOfficer = () => {
    return user?.role === 'COMMERCIAL_OFFICER';
  };

  const getHomePath = () => {
    if (user?.role === 'COMMERCIAL_OFFICER') return '/co';
    if (user?.role === 'ADMIN' || user?.role === 'COMPLIANCE') return '/admin';
    return '/dashboard';
  };

  const token = localStorage.getItem('youse_token');

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshToken,
    updateUser,
    refreshUser,
    isAdmin,
    isCommercialOfficer,
    getHomePath,
    token,
    sessionExpired,
    dismissSessionExpired,
  }), [user, loading, isAuthenticated, login, logout, refreshToken, token, sessionExpired, dismissSessionExpired]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
